import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import { notificar } from '../utils/notify';
import ContestacoesGestor from '../components/ContestacoesGestor';
import { radarPorCompetenciasIA, COMPETENCIAS_POR_TIPO, LABEL_COMPETENCIA, labelTipoReuniao } from '../utils/competencias';
import '../styles/memora_op.css';

// Tipos de análise (espelham os prompts da prompt_ia)
const TIPOS_REUNIAO = [
  { key: 'servico',   label: 'Serviço / Consultivo',   cor: '#d610b5', icone: 'fa-handshake' },
  { key: 'comercial', label: 'Comercial / Vendas',     cor: '#0d6efd', icone: 'fa-funnel-dollar' },
  { key: 'cs',        label: 'CS / Retenção',          cor: '#20c997', icone: 'fa-heart' },
  { key: 'gestao',    label: 'Gestão de Pessoas / 1:1', cor: '#6610f2', icone: 'fa-people-arrows' },
  { key: 'vision',    label: 'Reunião Vision',         cor: '#00205b', icone: 'fa-chart-pie' },
  { key: 'reforma_tributaria', label: 'Reforma Tributária', cor: '#b8860b', icone: 'fa-scale-balanced' },
];

const COMPETENCIAS = [
  { key: 'comunicacao', label: 'Comunicação',         palavras: ['comunicação','comunicar','clareza','claro','clara','expressão','didática','didático','vocabulário','linguagem','vícios','vício','articulação','tom','postura','objetividade'] },
  { key: 'tecnica',     label: 'Técnica / Produto',   palavras: ['técnico','técnica','conhecimento','plataforma','produto','sistema','solução','funcionalidade','funcionalidades','demonstração','demo','bi','dados','infraestrutura','migração','integração'] },
  { key: 'fechamento',  label: 'Fechamento',           palavras: ['fechamento','fechar','proposta','contrato','decisão','avançar','avançou','comprometimento','precificação','preço','próximos passos','plano comercial','perspectiva comercial','futuro comercial','negociação'] },
  { key: 'descoberta',  label: 'Descoberta de Dor',   palavras: ['dor','dores','necessidade','necessidades','problema','desafio','descoberta','qualificação','perguntas','investigar','diagnosticar','processos','perguntas abertas','entendimento','aprofundar','explorar'] },
  { key: 'objecoes',    label: 'Gestão de Objeções',  palavras: ['objeção','objeções','resistência','contornar','responder','lidar','evasiva','evasivo','tratada','gestão da objeção'] },
  { key: 'conducao',    label: 'Condução / Controle', palavras: ['condução','conduzir','controle','ritmo','agenda','fluiu','fluência','participação','engajado','engajamento','interveio','oportuna','oportunamente','complementando','alinhando','alinhamento','direcionou'] },
];

const CORES_OP = ['#0d6efd','#198754','#dc3545','#fd7e14','#6610f2','#20c997','#ffc107','#0dcaf0','#e83e8c','#6c757d'];
const SUGESTAO_TEXTO = {
  // categorias antigas (fallback por palavras-chave)
  'Descoberta de Dor':    'Praticar perguntas abertas e técnica dos 5 Porquês',
  'Comunicação':          'Reduzir vícios de linguagem, fazer pausas estratégicas',
  'Técnica / Produto':    'Aprofundar conhecimento do produto, revisão pré-reunião',
  'Condução / Controle':  'Estabelecer agenda clara e objetivo no início de cada reunião',
  // competências avaliadas pela Memora
  'Diagnóstico e Descoberta': 'Praticar perguntas abertas e investigação de causa raiz',
  'Proatividade':             'Levar recomendações e alertas preparados para cada reunião',
  'Domínio Técnico':          'Revisão técnica pré-reunião e estudo do contexto do cliente',
  'Tradução de Valor':        'Traduzir termos técnicos em impacto de negócio (R$, risco, prazo)',
  'Condução':                 'Abrir com agenda clara e controlar o ritmo da reunião',
  'Gestão de Objeções':       'Desenvolver script para as objeções mais comuns',
  'Compromisso':              'Fechar cada assunto com dono e prazo definidos',
  'Orientação a Dados':       'Levar BI e indicadores — basear a conversa em números',
  'Qualificação':             'Mapear decisor, orçamento e prazo em toda oportunidade',
  'Fechamento':               'Treinar pedido de avanço e próximos passos com data',
  'Apresentação do Produto':  'Personalizar a demonstração à realidade do cliente',
  'Empatia e Escuta':         'Praticar escuta ativa e validação antes de responder',
  // gestão de pessoas / 1:1
  'Escuta Ativa':               'Deixar o liderado falar mais — perguntar e explorar antes de responder',
  'Perguntas Poderosas':        'Levar 2-3 perguntas abertas sobre motivação e carreira para cada 1:1',
  'Feedback de Qualidade':      'Dar feedback com exemplo concreto e comportamento observável',
  'Desenvolvimento e Carreira': 'Reservar parte da 1:1 para PDI e próximos desafios do liderado',
  'Remoção de Bloqueios':       'Perguntar "o que está te travando?" e assumir ações para destravar',
  'Segurança Psicológica':      'Acolher problemas sem minimizar nem transformar em cobrança',
  'Reconhecimento':             'Reconhecer entregas específicas em toda 1:1',
  'Follow-through da Gestão':   'Retomar os combinados da 1:1 anterior e fechar com ações claras',
  'Participação Ativa':         'Trazer pauta própria para a 1:1 — não esperar a pauta do gestor',
  'Transparência':              'Expor dificuldades e riscos reais em vez de "está tudo bem"',
  'Protagonismo':               'Propor soluções e pedir o que precisa, em vez de só relatar',
  'Receptividade a Feedback':   'Explorar o feedback recebido com perguntas antes de se justificar',
  'Clareza de Prioridades':     'Validar metas e prioridades com o gestor quando houver dúvida',
};

// Rótulo do radar: quebra nomes longos em até 2 linhas e afasta do gráfico
// para não sobrepor quando há muitas competências
const TickRadar = ({ payload, x, y, cx, cy, textAnchor }) => {
  const texto = String(payload?.value || '');
  const palavras = texto.split(' ');
  const linhas = [];
  let atual = '';
  palavras.forEach(p => {
    if (atual && (atual.length + p.length + 1) > 14) {
      linhas.push(atual);
      atual = p;
    } else {
      atual = atual ? `${atual} ${p}` : p;
    }
  });
  if (atual) linhas.push(atual);

  // Empurra o rótulo radialmente para fora (afasta da malha)
  const fator = 1.06;
  const nx = cx + (x - cx) * fator;
  const ny = cy + (y - cy) * fator;
  // Ajuste vertical para centralizar o bloco de linhas no ponto
  const dyInicial = ny > cy + 8 ? 8 : ny < cy - 8 ? -((linhas.length - 1) * 11) : -((linhas.length - 1) * 5.5) + 4;

  return (
    <text x={nx} y={ny} textAnchor={textAnchor} fontSize={11.5} fill="#495057" fontWeight={500}>
      {linhas.map((l, i) => (
        <tspan key={i} x={nx} dy={i === 0 ? dyInicial : 12}>{l}</tspan>
      ))}
    </text>
  );
};

const normText = (s = '') => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const incluiAlguma = (texto, palavras) => palavras.some(p => normText(texto).includes(normText(p)));

// Normaliza o tipo_reuniao gravado no banco para servico | comercial | cs | gestao
const normTipo = (t) => {
  const x = normText(t || '');
  if (x.includes('comercial')) return 'comercial';
  if (x.includes('cs') || x.includes('retencao')) return 'cs';
  if (x.includes('gestao') || x.includes('1:1') || x.includes('one')) return 'gestao';
  if (x.includes('servico')) return 'servico';
  return x;
};

const scoreComp = (lista, comp) => {
  let pos = 0, neg = 0;
  lista.forEach(a => {
    if (a.ponto_forte    && incluiAlguma(a.ponto_forte,    comp.palavras)) pos++;
    if (a.ponto_melhoria && incluiAlguma(a.ponto_melhoria, comp.palavras)) neg++;
  });
  const total = pos + neg;
  return total === 0 ? null : Math.round(((pos / total) * 10) * 10) / 10;
};

const agrupar = (lista, modo) => {
  const mapa = {};
  lista.forEach(a => {
    const raw = (a.data_reuniao || a.created_at || '').replace(' ', 'T');
    const dt = new Date(raw);
    if (!raw || isNaN(dt)) return;
    let chave;
    if (modo === 'semanal') {
      const ini = new Date(dt);
      ini.setDate(dt.getDate() - dt.getDay());
      chave = ini.toISOString().slice(0, 10);
    } else if (modo === 'diario') {
      chave = raw.slice(0, 10);
    } else {
      chave = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    }
    if (!mapa[chave]) mapa[chave] = { periodo: chave, notas: [], count: 0 };
    mapa[chave].notas.push(parseFloat(a.nota) || 0);
    mapa[chave].count++;
  });
  return Object.values(mapa).sort((a, b) => a.periodo.localeCompare(b.periodo))
    .map(g => ({ periodo: g.periodo, media: Math.round((g.notas.reduce((s, n) => s + n, 0) / g.notas.length) * 10) / 10, count: g.count }));
};

const fmtPeriodo = (chave, modo) => {
  if (modo === 'mensal') {
    const [ano, mes] = chave.split('-');
    return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(mes)-1]}/${ano.slice(2)}`;
  }
  const p = chave.slice(0, 10).split('-');
  return `${p[2]}/${p[1]}`;
};

const fmtData = (d) => {
  if (!d) return '-';
  const p = String(d).slice(0, 10).split('-');
  return p.length < 3 ? d : `${p[2]}/${p[1]}/${p[0]}`;
};

const badgeNota = (nota) => {
  const n = parseFloat(nota) || 0;
  if (n >= 8) return 'bg-success';
  if (n >= 6) return 'bg-primary';
  if (n >= 4) return 'bg-warning text-dark';
  return 'bg-danger';
};

const corScore = (s) => s === null ? '#adb5bd' : s >= 7 ? '#198754' : s >= 5 ? '#fd7e14' : '#dc3545';

// Estilo dos chips de filtro (setor / análise / operador)
const chipStyle = (ativo, cor) => ({
  borderRadius: 50,
  padding: '5px 14px',
  fontWeight: 600,
  fontSize: '0.8rem',
  border: `1.5px solid ${ativo ? cor : '#dee2e6'}`,
  background: ativo ? cor : '#fff',
  color: ativo ? '#fff' : '#495057',
  boxShadow: ativo ? `0 2px 8px ${cor}55` : 'none',
  transition: 'all .15s ease',
});

const ChipBadge = ({ ativo, children }) => (
  <span
    className="badge ms-1"
    style={{
      background: ativo ? 'rgba(255,255,255,0.25)' : '#f1f3f5',
      color: ativo ? '#fff' : '#495057',
      fontWeight: 700,
    }}
  >
    {children}
  </span>
);

const TooltipEvol = ({ active, payload, label, modo }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="card shadow-sm" style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
      <strong>{fmtPeriodo(label, modo)}</strong>
      <div>Média: <strong>{payload[0].value}/10</strong></div>
      <div>Reuniões: {payload[0].payload.count}</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const Analise_gestor_memora = () => {
  const [analises, setAnalises]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [mensagem, setMensagem]             = useState('');
  const [periodo, setPeriodo]               = useState('diario');
  const [filtroSetor, setFiltroSetor]       = useState('');
  const [filtroTipo, setFiltroTipo]         = useState('');
  const [filtroOp, setFiltroOp]             = useState('');
  const [detalhe, setDetalhe]               = useState(null);
  const [tblOperador, setTblOperador]       = useState('');
  const [tblCodigo, setTblCodigo]           = useState('');
  const [tblEmpresa, setTblEmpresa]         = useState('');
  const [tblTipo, setTblTipo]               = useState('');
  const [tblData, setTblData]               = useState('');
  const [tblNota, setTblNota]               = useState('');
  const [analiseTime, setAnaliseTime]                 = useState('');
  const [carregandoAnaliseTime, setCarregandoAnaliseTime] = useState(false);
  const [showModalAnaliseTime, setShowModalAnaliseTime]   = useState(false);
  const navigate = useNavigate();

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const SETORES = ['Contábil', 'BPO Financeiro', 'Fiscal', 'Departamento Pessoal', 'Comercial', 'CaaS', 'Tecnologia', 'Marketing', 'CS'];

  const [gestorSetor, setGestorSetor] = useState(null); // setor de gestão das 1:1 (ou 'Todos')

  useEffect(() => {
    const carregar = async () => {
      const token = localStorage.getItem('access');
      try {
        const [res, resGrupo] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE}/api/reunioes/analise-gestor/`,
            { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
            { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        ]);
        if (res.data.success) {
          setAnalises(res.data.analises);
        } else setMensagem('Nenhuma análise encontrada.');
        if (resGrupo?.data?.gestor) setGestorSetor(resGrupo.data.gestor);
      } catch (err) {
        setMensagem('' + (err.response?.data?.erro || err.message));
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, []);

  // O usuário vê todas as 1:1 (Todos/Geral/Diretoria) ou só as do seu setor?
  const gestorVeTudo = ['full', 'todos', 'geral', 'diretoria', 'todos os setores']
    .includes(normText(gestorSetor || ''));

  // Cadeia de filtros: setor → tipo de análise → operador
  const analisesPorSetor = useMemo(() => {
    if (!filtroSetor) return analises;
    const alvo = normText(filtroSetor);
    return analises.filter(a => normText(a.setor || '') === alvo);
  }, [analises, filtroSetor]);

  const baseFiltro = useMemo(() => {
    if (!filtroTipo) return analisesPorSetor;
    return analisesPorSetor.filter(a => normTipo(a.tipo_reuniao) === filtroTipo);
  }, [analisesPorSetor, filtroTipo]);

  const operadores = useMemo(() =>
    [...new Set(baseFiltro.map(a => a.operador).filter(Boolean))].sort(),
    [baseFiltro]
  );

  // quando troca setor/tipo, ignora filtro de operador se o op não pertence mais à base
  const filtradas = useMemo(() => {
    const base = filtroOp && operadores.includes(filtroOp)
      ? baseFiltro.filter(a => a.operador === filtroOp)
      : baseFiltro;
    return base;
  }, [baseFiltro, filtroOp, operadores]);

  // Há avaliações de competência da Memora na base?
  const temIA = useMemo(() =>
    analises.some(a => Array.isArray(a.competencias) && a.competencias.length > 0),
    [analises]
  );

  // KPIs — respeitam todos os filtros (setor, tipo e operador)
  const kpis = useMemo(() => {
    if (!filtradas.length) return null;
    const notas = filtradas.map(a => parseFloat(a.nota) || 0);
    const mediaGeral = Math.round((notas.reduce((s, n) => s + n, 0) / notas.length) * 10) / 10;
    const mapaOp = {};
    filtradas.forEach(a => {
      const op = a.operador || '?';
      if (!mapaOp[op]) mapaOp[op] = [];
      mapaOp[op].push(parseFloat(a.nota) || 0);
    });
    const mediasOp = Object.entries(mapaOp).map(([op, ns]) => ({ op, media: ns.reduce((s, n) => s + n, 0) / ns.length }));
    const top  = [...mediasOp].sort((a, b) => b.media - a.media)[0];
    const pior = [...mediasOp].sort((a, b) => a.media - b.media)[0];
    const agora = Date.now(), d7 = 604800000;
    const rec = filtradas.filter(a => { const dt = new Date((a.data_reuniao||a.created_at||'').replace(' ','T')); return agora-dt.getTime() <= d7; });
    const ant = filtradas.filter(a => { const dt = new Date((a.data_reuniao||a.created_at||'').replace(' ','T')); const df = agora-dt.getTime(); return df > d7 && df <= 2*d7; });
    let tend = null;
    if (rec.length && ant.length) {
      const mR = rec.reduce((s,a)=>s+(parseFloat(a.nota)||0),0)/rec.length;
      const mA = ant.reduce((s,a)=>s+(parseFloat(a.nota)||0),0)/ant.length;
      tend = { valor: Math.round((mR-mA)*10)/10, mRec: Math.round(mR*10)/10, mAnt: Math.round(mA*10)/10 };
    }
    return {
      mediaGeral, total: filtradas.length,
      pctAcima8:  Math.round(notas.filter(n=>n>=8).length/notas.length*100),
      pctAbaixo6: Math.round(notas.filter(n=>n<6).length/notas.length*100),
      topOp: top?.op, topMedia: Math.round((top?.media||0)*10)/10,
      piorOp: pior?.op, piorMedia: Math.round((pior?.media||0)*10)/10,
      tend,
    };
  }, [filtradas]);

  // Evolução
  const dadosEvol = useMemo(() => agrupar(filtradas, periodo), [filtradas, periodo]);

  // Insights
  const insights = useMemo(() => {
    const msgs = [];
    if (dadosEvol.length >= 2) {
      const ult = dadosEvol[dadosEvol.length-1], pen = dadosEvol[dadosEvol.length-2];
      const diff = ult.media - pen.media;
      const pct = pen.media > 0 ? Math.abs(diff/pen.media*100).toFixed(0) : 0;
      if (diff <= -0.5) msgs.push({ tipo:'danger',  txt:`Time caiu ${pct}% no último período (${pen.media} → ${ult.media})` });
      else if (diff >= 0.5) msgs.push({ tipo:'success', txt:`Time evoluiu ${pct}% no último período (${pen.media} → ${ult.media})` });
    }
    if (kpis?.pctAbaixo6 >= 20) msgs.push({ tipo:'warning', txt:`${kpis.pctAbaixo6}% das reuniões abaixo de 6 — atenção redobrada` });
    if (kpis?.tend?.valor <= -0.5) msgs.push({ tipo:'danger', txt:`Tendência semanal negativa: ${kpis.tend.mAnt} → ${kpis.tend.mRec}` });
    return msgs;
  }, [dadosEvol, kpis]);

  // Gargalos e fortes — dados da Memora quando existem; senão, palavras-chave
  const contarCompetencias = (lista, condicao) => {
    const chavesDoTipo = filtroTipo && COMPETENCIAS_POR_TIPO[filtroTipo] ? COMPETENCIAS_POR_TIPO[filtroTipo] : null;
    const freq = {};
    lista.forEach(a => {
      (a.competencias || []).forEach(c => {
        if (!c || !c.competencia || c.status === 'nao_se_aplica') return;
        if (chavesDoTipo && !chavesDoTipo.includes(c.competencia)) return;
        const nota = parseFloat(c.nota);
        if (!condicao(isNaN(nota) ? null : nota, c.status)) return;
        const label = LABEL_COMPETENCIA[c.competencia] || c.competencia;
        freq[label] = (freq[label] || 0) + 1;
      });
    });
    return Object.entries(freq).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  };

  // Gargalos por TAXA de falha (falhas ÷ vezes que a competência foi avaliada),
  // não pela contagem bruta — assim uma competência muito avaliada não vira
  // gargalo só por acumular falhas. Exige um mínimo de avaliações para o % ser confiável.
  const MIN_AVALIACOES_GARGALO = 3;
  const dadosGargalos = useMemo(() => {
    if (temIA) {
      const chavesDoTipo = filtroTipo && COMPETENCIAS_POR_TIPO[filtroTipo] ? COMPETENCIAS_POR_TIPO[filtroTipo] : null;
      const stats = {}; // label -> { falhas, total }
      filtradas.forEach(a => {
        (a.competencias || []).forEach(c => {
          if (!c || !c.competencia || c.status === 'nao_se_aplica') return;
          if (chavesDoTipo && !chavesDoTipo.includes(c.competencia)) return;
          const label = LABEL_COMPETENCIA[c.competencia] || c.competencia;
          const nota = parseFloat(c.nota);
          const falhou = c.status === 'nao_demonstrou' || (!isNaN(nota) && nota < 7);
          if (!stats[label]) stats[label] = { falhas: 0, total: 0 };
          stats[label].total += 1;
          if (falhou) stats[label].falhas += 1;
        });
      });
      return Object.entries(stats)
        .filter(([, v]) => v.total >= MIN_AVALIACOES_GARGALO && v.falhas > 0)
        .map(([name, v]) => ({ name, value: Math.round((v.falhas / v.total) * 100), falhas: v.falhas, total: v.total }))
        .sort((a, b) => b.value - a.value);
    }
    // Fallback sem IA: usa palavras-chave de pontos de melhoria (contagem bruta mesmo)
    const freq = {};
    filtradas.forEach(a => { if (!a.ponto_melhoria) return; COMPETENCIAS.forEach(c => { if (incluiAlguma(a.ponto_melhoria, c.palavras)) freq[c.label] = (freq[c.label]||0)+1; }); });
    return Object.entries(freq).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
  }, [filtradas, temIA, filtroTipo]);

  const dadosFortes = useMemo(() => {
    if (temIA) {
      return contarCompetencias(filtradas, (nota) => nota !== null && nota >= 7);
    }
    const freq = {};
    filtradas.forEach(a => { if (!a.ponto_forte) return; COMPETENCIAS.forEach(c => { if (incluiAlguma(a.ponto_forte, c.palavras)) freq[c.label] = (freq[c.label]||0)+1; }); });
    return Object.entries(freq).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
  }, [filtradas, temIA, filtroTipo]);

  // Impacto palavras
  const { impactoPos, impactoNeg } = useMemo(() => {
    if (!filtradas.length) return { impactoPos:[], impactoNeg:[] };
    const mg = filtradas.reduce((s,a)=>s+(parseFloat(a.nota)||0),0)/filtradas.length;
    const calc = (campo) => {
      const mapa = {};
      filtradas.forEach(a => {
        const nota = parseFloat(a.nota)||0;
        (a[campo]||[]).forEach(item => {
          const p = normText(typeof item==='string'?item:item?.palavra||'');
          if (!p) return;
          if (!mapa[p]) mapa[p] = { notas:[], count:0 };
          mapa[p].notas.push(nota); mapa[p].count++;
        });
      });
      return Object.entries(mapa).filter(([,v])=>v.count>=2).map(([palavra,v])=>({
        palavra, count:v.count,
        impacto: Math.round((v.notas.reduce((s,n)=>s+n,0)/v.notas.length - mg)*10)/10,
      }));
    };
    return {
      impactoPos: calc('palavras_positivas').sort((a,b)=>b.impacto-a.impacto).slice(0,12),
      impactoNeg: calc('palavras_negativas').sort((a,b)=>a.impacto-b.impacto).slice(0,12),
    };
  }, [filtradas]);

  // Colunas de competência da tabela comparativa: com dados da Memora usa as
  // do tipo filtrado (ou a união das presentes); senão as categorias antigas
  const colunasComp = useMemo(() => {
    if (!temIA) return COMPETENCIAS.map(c => ({ key: c.key, label: c.label }));
    let chaves;
    if (filtroTipo && COMPETENCIAS_POR_TIPO[filtroTipo]) {
      chaves = COMPETENCIAS_POR_TIPO[filtroTipo];
    } else {
      const set = new Set();
      filtradas.forEach(a => (a.competencias || []).forEach(c => c?.competencia && set.add(c.competencia)));
      const ordem = Object.keys(LABEL_COMPETENCIA);
      chaves = [...set].sort((a, b) => ordem.indexOf(a) - ordem.indexOf(b));
    }
    return chaves.map(k => ({ key: k, label: LABEL_COMPETENCIA[k] || k }));
  }, [temIA, filtroTipo, filtradas]);

  // Comparação operadores — médias por competência (Memora) ou score por palavras-chave
  const comparacaoOp = useMemo(() =>
    (filtroOp ? operadores.filter(o => o === filtroOp) : operadores).map((op, idx) => {
      const anOp = filtradas.filter(a=>a.operador===op);
      const media = anOp.length ? Math.round((anOp.reduce((s,a)=>s+(parseFloat(a.nota)||0),0)/anOp.length)*10)/10 : 0;
      const scores = {};
      if (temIA) {
        colunasComp.forEach(c => {
          const notas = [];
          anOp.forEach(a => (a.competencias || []).forEach(cc => {
            if (cc?.competencia === c.key && cc.status !== 'nao_se_aplica') {
              const n = parseFloat(cc.nota);
              if (!isNaN(n)) notas.push(n);
            }
          }));
          scores[c.key] = notas.length
            ? Math.round((notas.reduce((s, n) => s + n, 0) / notas.length) * 10) / 10
            : null;
        });
      } else {
        COMPETENCIAS.forEach(c => { scores[c.key] = scoreComp(anOp, c); });
      }
      return { operador:op, media, total:anOp.length, cor: CORES_OP[idx%CORES_OP.length], ...scores };
    }).sort((a,b)=>b.media-a.media),
    [filtradas, filtroOp, operadores, temIA, colunasComp]
  );

  // Radar de competências (time inteiro ou operador filtrado)
  // Preferência: competências avaliadas pela Memora, filtradas pelo tipo; fallback: palavras-chave
  const dadosRadar = useMemo(() => {
    const ia = radarPorCompetenciasIA(filtradas, filtroTipo || null);
    if (ia && ia.length >= 3) return ia;
    return COMPETENCIAS.map(c => ({
      competencia: c.label,
      score: scoreComp(filtradas, c) ?? 5,
    }));
  }, [filtradas, filtroTipo]);

  // Reuniões críticas e exemplares
  const reunioesCriticas   = useMemo(()=>filtradas.filter(a=>parseFloat(a.nota)<6).sort((a,b)=>parseFloat(a.nota)-parseFloat(b.nota)).slice(0,10), [filtradas]);
  const reunioesExemplares = useMemo(()=>filtradas.filter(a=>parseFloat(a.nota)>=9).sort((a,b)=>parseFloat(b.nota)-parseFloat(a.nota)).slice(0,6), [filtradas]);

  // Média por cliente + objeções/produtos — calculada a partir de filtradas, então
  // respeita todos os filtros (setor, tipo de análise, operador). A nota é da REUNIÃO
  // (por codigo_reuniao), não por operador, para não duplicar quando há vários participantes.
  const clientesMedias = useMemo(() => {
    const mapa = {};
    filtradas.forEach(a => {
      const emp = (a.cliente_empresa || '').trim();
      if (!emp) return;
      if (!mapa[emp]) mapa[emp] = { reunioes: {}, objecoes: {}, produtos: {} };
      const cod = a.codigo_reuniao || `__${emp}`;
      // uma nota por reunião (usa a nota da reunião)
      const notaR = parseFloat(a.nota_reuniao);
      if (!isNaN(notaR)) mapa[emp].reunioes[cod] = notaR;
      // objeções e produtos (agregados, contando por reunião única)
      if (!mapa[emp][`_vistas_${cod}`]) {
        mapa[emp][`_vistas_${cod}`] = true;
        (a.objecoes || []).forEach(o => {
          const t = typeof o === 'string' ? o : (o?.objecao || o?.descricao || '');
          if (t) mapa[emp].objecoes[t] = (mapa[emp].objecoes[t] || 0) + 1;
        });
        (a.produtos_servicos || []).forEach(p => {
          const t = typeof p === 'string' ? p : (p?.nome || '');
          if (t) mapa[emp].produtos[t] = (mapa[emp].produtos[t] || 0) + 1;
        });
      }
    });
    const topN = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);
    return Object.entries(mapa).map(([empresa, v]) => {
      const notas = Object.values(v.reunioes);
      const media = notas.length ? Math.round((notas.reduce((s, n) => s + n, 0) / notas.length) * 100) / 100 : 0;
      return {
        cliente_empresa: empresa,
        media_nota: media,
        total_reunioes: notas.length,
        top_objecoes: topN(v.objecoes),
        top_produtos: topN(v.produtos),
      };
    }).filter(c => c.total_reunioes > 0).sort((a, b) => a.media_nota - b.media_nota);
  }, [filtradas]);

  // Sugestões — pior competência de cada operador (mesmas colunas da comparação)
  const sugestoes = useMemo(()=>
    comparacaoOp.map(op => {
      let piorComp='Geral', piorScore=11;
      colunasComp.forEach(c=>{ const s=op[c.key]; if(s!==null && s!==undefined && s<piorScore){ piorScore=s; piorComp=c.label; } });
      const anOp = filtradas.filter(a=>a.operador===op.operador);
      const rec = anOp.slice(0,3).map(a=>parseFloat(a.nota)||0);
      const ant = anOp.slice(3,6).map(a=>parseFloat(a.nota)||0);
      let evo = 0;
      if (rec.length && ant.length) {
        const mR = rec.reduce((s,n)=>s+n,0)/rec.length;
        const mA = ant.reduce((s,n)=>s+n,0)/ant.length;
        evo = Math.round((mR-mA)*10)/10;
      }
      return { ...op, sugestao:piorComp, piorScore, evo };
    }),
    [comparacaoOp, filtradas, colunasComp]
  );

  // ── Narração em áudio (Gemini TTS) — mesmo padrão do Painel do Operador ──
  const [audioPartes, setAudioPartes] = useState([]);
  const [totalPartes, setTotalPartes] = useState(0);
  const [parteAtual, setParteAtual]   = useState(0);
  const [gerandoAudio, setGerandoAudio] = useState(false);
  const [velocAudio, setVelocAudio]   = useState(1);
  const audioRef = useRef(null);

  // Gargalos/Fortes: mostra só os 5 principais, com "ver todas"
  const [expandirSecao, setExpandirSecao] = useState({});

  // 1ª parte curta (~600 chars) pra voz começar em segundos; demais maiores
  const dividirEmPartes = (texto) => {
    const blocos = [];
    texto.split(/\n{2,}/).forEach(par => {
      if (par.length <= 600) { blocos.push(par); return; }
      let frase = '';
      par.split(/(?<=[.!?:])\s+/).forEach(fr => {
        if (frase && (frase.length + fr.length) > 600) { blocos.push(frase); frase = fr; }
        else frase = frase ? `${frase} ${fr}` : fr;
      });
      if (frase) blocos.push(frase);
    });
    const partes = [];
    let atual = '';
    blocos.forEach(b => {
      const limite = partes.length === 0 ? 600 : 1800;
      if (atual && (atual.length + b.length) > limite) { partes.push(atual); atual = b; }
      else atual = atual ? `${atual}\n\n${b}` : b;
    });
    if (atual.trim()) partes.push(atual);
    return partes;
  };

  const gerarAudio = (texto) => {
    if (!texto || gerandoAudio) return;
    const partes = dividirEmPartes(texto);
    if (!partes.length) return;
    setGerandoAudio(true);
    setTotalPartes(partes.length);
    setAudioPartes(new Array(partes.length).fill(null));
    setParteAtual(0);
    const token = localStorage.getItem('access');
    partes.forEach((textoParte, i) => {
      axios.post(
        `${process.env.REACT_APP_API_BASE}/api/reunioes/gerar-audio/`,
        { texto: textoParte },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(res => {
        if (res.data.success) {
          setAudioPartes(prev => {
            const c = [...prev];
            c[i] = `data:${res.data.mime};base64,${res.data.audio}`;
            return c;
          });
        } else if (i === 0) {
          notificar('' + (res.data.erro || 'Erro ao gerar o áudio.'));
          setGerandoAudio(false);
          setTotalPartes(0);
        }
      }).catch(err => {
        if (i === 0) {
          notificar('' + (err.response?.data?.erro || err.message));
          setGerandoAudio(false);
          setTotalPartes(0);
        }
      });
    });
  };

  useEffect(() => {
    if (gerandoAudio && audioPartes[0]) setGerandoAudio(false);
  }, [audioPartes, gerandoAudio]);

  const mudarVelocidade = (v) => {
    setVelocAudio(v);
    if (audioRef.current) audioRef.current.playbackRate = v;
  };

  const renderBarraAudio = (texto) => {
    if (!texto || texto.startsWith('')) return null;
    return (
      <div className="p-3 rounded-3 mb-3 d-flex align-items-center gap-3 flex-wrap"
        style={{ background: '#f0faf5', border: '1px solid #badbcc' }}>
        {totalPartes === 0 || gerandoAudio ? (
          <>
            <button
              className="btn btn-sm fw-semibold"
              onClick={() => gerarAudio(texto)}
              disabled={gerandoAudio}
              style={{
                background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                border: 'none', color: '#fff',
              }}
            >
              {gerandoAudio
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Deixa comigo, já vou falar...</>
                : <><i className="fas fa-volume-up me-1"></i>Quer que eu fale pra você?</>}
            </button>
            <small className="text-muted">
              {gerandoAudio
                ? <>Só um instante, estou organizando as ideias — começo a falar em segundos. <i className="fas fa-microphone ms-1"></i></>
                : 'Sem tempo de ler? Sou eu, a Memor.IA — aperta aí que eu te conto como está o time. '}
            </small>
          </>
        ) : audioPartes[parteAtual] ? (
          <>
            <audio
              ref={audioRef}
              key={parteAtual}
              src={audioPartes[parteAtual]}
              controls
              autoPlay
              style={{ height: 38, flexGrow: 1, minWidth: 240 }}
              onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = velocAudio; }}
              onEnded={() => {
                if (parteAtual + 1 < totalPartes) setParteAtual(p => p + 1);
              }}
            ></audio>
            {totalPartes > 1 && (
              <span className="badge bg-secondary">Parte {parteAtual + 1}/{totalPartes}</span>
            )}
            <div className="btn-group btn-group-sm">
              {[1, 1.25, 1.5].map(v => (
                <button
                  key={v}
                  className={`btn ${velocAudio === v ? '' : 'btn-outline-secondary'}`}
                  style={velocAudio === v ? { backgroundColor: '#198754', borderColor: '#198754', color: '#fff' } : undefined}
                  onClick={() => mudarVelocidade(v)}
                >
                  {v}x
                </button>
              ))}
            </div>
          </>
        ) : (
          <small className="text-muted">
            <span className="spinner-border spinner-border-sm me-2"></span>
            Pegando fôlego para a parte {parteAtual + 1}/{totalPartes}... <i className="fas fa-microphone ms-1"></i>
          </small>
        )}
      </div>
    );
  };

  // ── Diagnóstico do time pela Memor.IA ────────────────────────────────────
  const pedirAnaliseTime = async () => {
    setAnaliseTime('');
    setShowModalAnaliseTime(true);
    setCarregandoAnaliseTime(true);
    setAudioPartes([]);
    setTotalPartes(0);
    setParteAtual(0);
    setVelocAudio(1);
    const token = localStorage.getItem('access');
    try {
      const payload = {
        setor: filtroSetor || 'Todos os setores',
        tipo: filtroTipo
          ? (TIPOS_REUNIAO.find(t => t.key === filtroTipo)?.label || filtroTipo)
          : 'Todos os tipos de análise',
        dados: {
          total_analises: filtradas.length,
          media_geral: kpis?.mediaGeral,
          pct_acima_8: kpis?.pctAcima8,
          pct_abaixo_6: kpis?.pctAbaixo6,
          evolucao_mensal: agrupar(filtradas, 'mensal'),
          gargalos: dadosGargalos,
          pontos_fortes: dadosFortes,
          clientes_nota_baixa: clientesMedias
            .filter(c => (parseFloat(c.media_nota) || 0) < 7)
            .slice(0, 15),
          operadores: comparacaoOp.map(o => ({
            operador: o.operador,
            media: o.media,
            reunioes: o.total,
            competencias: Object.fromEntries(
              colunasComp.map(c => [c.label, o[c.key] ?? null])
            ),
          })),
          reunioes_criticas: reunioesCriticas.slice(0, 8).map(a => ({
            operador: a.operador,
            cliente: a.cliente_empresa,
            nota: a.nota,
            ponto_melhoria: a.ponto_melhoria,
          })),
        },
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/reunioes/analise-time/`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) setAnaliseTime(res.data.analise);
      else setAnaliseTime('' + (res.data.erro || 'Erro ao gerar o diagnóstico.'));
    } catch (err) {
      setAnaliseTime('' + (err.response?.data?.erro || err.message));
    } finally {
      setCarregandoAnaliseTime(false);
    }
  };

  // Renderizador simples de markdown (mesmo padrão das outras páginas)
  const renderMarkdown = (texto) => {
    if (!texto) return null;
    const linhas = texto.split('\n');
    const resultado = [];
    let listaAtual = [];
    const flushLista = () => {
      if (listaAtual.length > 0) {
        resultado.push(<ul key={`ul-${resultado.length}`} style={{ paddingLeft: 22, marginBottom: 14 }}>{listaAtual}</ul>);
        listaAtual = [];
      }
    };
    const renderInline = (text, key) => {
      const partes = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
      return (
        <span key={key}>
          {partes.map((p, i) => {
            if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
            if (p.startsWith('*') && p.endsWith('*')) return <em key={i} style={{ color: '#555' }}>{p.slice(1, -1)}</em>;
            return p;
          })}
        </span>
      );
    };
    linhas.forEach((linha, i) => {
      if (linha.startsWith('## ')) {
        flushLista();
        resultado.push(<h5 key={i} className="fw-bold mt-4 mb-2 pb-2" style={{ borderBottom: '2px solid #dee2e6', fontSize: '1.2rem' }}>{linha.slice(3)}</h5>);
      } else if (linha.startsWith('### ')) {
        flushLista();
        resultado.push(<h6 key={i} className="fw-bold mt-3 mb-1" style={{ color: '#6610f2', fontSize: '1.05rem' }}>{linha.slice(4)}</h6>);
      } else if (/^---+$/.test(linha.trim())) {
        flushLista();
        resultado.push(<hr key={i} className="my-3" />);
      } else if (/^[\*\-]\s+/.test(linha)) {
        listaAtual.push(<li key={i} style={{ marginBottom: 6, lineHeight: 1.7, fontSize: '1rem' }}>{renderInline(linha.replace(/^[\*\-]\s+/, ''), `li-${i}`)}</li>);
      } else if (/^\d+\.\s+/.test(linha)) {
        flushLista();
        const num = linha.match(/^(\d+)\.\s+(.*)/);
        if (num) resultado.push(
          <div key={i} className="d-flex gap-2 mb-2 align-items-start">
            <span className="badge rounded-pill" style={{ background: '#00205b', minWidth: 24, fontSize: '0.85rem', flexShrink: 0, marginTop: 3 }}>{num[1]}</span>
            <span style={{ fontSize: '1rem', lineHeight: 1.7 }}>{renderInline(num[2], `num-${i}`)}</span>
          </div>
        );
      } else if (linha.trim() === '') {
        flushLista();
        resultado.push(<div key={i} style={{ height: 8 }} />);
      } else {
        flushLista();
        resultado.push(<p key={i} className="mb-1" style={{ fontSize: '1rem', lineHeight: 1.8 }}>{renderInline(linha, `p-${i}`)}</p>);
      }
    });
    flushLista();
    return resultado;
  };

  // Histórico filtrado pela tabela
  const historicoFiltrado = useMemo(() => {
    return filtradas.filter(a => {
      const n = normText;
      if (tblOperador && !n(a.operador || '').includes(n(tblOperador))) return false;
      if (tblCodigo  && !n(a.codigo_reuniao || '').includes(n(tblCodigo))) return false;
      if (tblEmpresa && !n(a.cliente_empresa || '').includes(n(tblEmpresa))) return false;
      if (tblTipo    && !n(a.tipo_reuniao || '').includes(n(tblTipo))) return false;
      if (tblData    && !(a.data_reuniao || '').includes(tblData)) return false;
      if (tblNota) {
        const nota = parseFloat(a.nota) || 0;
        if (tblNota === '>=8' && nota < 8) return false;
        if (tblNota === '6-8' && (nota < 6 || nota >= 8)) return false;
        if (tblNota === '<6'  && nota >= 6) return false;
      }
      return true;
    });
  }, [filtradas, tblOperador, tblCodigo, tblEmpresa, tblTipo, tblData, tblNota]);

  // Histórico agrupado por reunião: uma linha por código, com todos os operadores
  const historicoAgrupado = useMemo(() => {
    const mapa = {};
    historicoFiltrado.forEach(a => {
      const k = a.codigo_reuniao || `sem-codigo-${a.id}`;
      if (!mapa[k]) mapa[k] = { ...a, operadores_reuniao: [] };
      if (a.operador) mapa[k].operadores_reuniao.push({ nome: a.operador, nota: a.nota });
    });
    return Object.values(mapa);
  }, [historicoFiltrado]);

  // Modal com a análise completa da reunião (dados + todos os operadores)
  const [reuniaoDetalhe, setReuniaoDetalhe] = useState(null);
  const [carregandoReuniao, setCarregandoReuniao] = useState(false);
  const abrirReuniao = async (codigo) => {
    if (!codigo) return;
    setCarregandoReuniao(true);
    setReuniaoDetalhe({ carregando: true });
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reunioes/detalhe/${encodeURIComponent(codigo)}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) setReuniaoDetalhe({ reuniao: res.data.reuniao, operadores: res.data.operadores });
      else setReuniaoDetalhe(null);
    } catch (e) {
      setReuniaoDetalhe(null);
    } finally {
      setCarregandoReuniao(false);
    }
  };

  // Modal
  const renderModal = () => {
    if (!detalhe) return null;
    const a = detalhe;
    return (
      <div className="modal fade show d-block" style={{backgroundColor:'rgba(0,0,0,0.55)'}} onClick={()=>setDetalhe(null)}>
        <div className="modal-dialog modal-dialog-scrollable" style={{maxWidth:'700px'}} onClick={e=>e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header" style={{backgroundColor:'#ffffff', borderBottom:'1px solid #e9ecef'}}>
              <h5 className="modal-title mb-0 fw-bold" style={{color:'#212529'}}><i className="fas fa-search me-2"></i>{a.codigo_reuniao} — {a.operador}</h5>
              <button className="btn-close" onClick={()=>setDetalhe(null)}></button>
            </div>
            <div className="modal-body">
              <div className="row g-3 mb-3">
                {[
                  {l:'Nota',   v:<span className={`badge fs-5 ${badgeNota(a.nota)}`}>{a.nota}/10</span>},
                  {l:'Data',   v:<strong>{fmtData(a.data_reuniao)}</strong>},
                  {l:'Cliente',v:<strong style={{fontSize:'0.85rem'}}>{a.cliente_empresa||'-'}</strong>},
                  {l:'Tipo',   v:<span className="badge bg-secondary">{a.tipo_reuniao||'-'}</span>},
                ].map((item,i)=>(
                  <div key={i} className="col-6 col-md-3 text-center">
                    <small className="text-muted d-block">{item.l}</small>{item.v}
                  </div>
                ))}
              </div>
              {a.ponto_forte    && <div className="alert alert-success"><strong><i className="fas fa-thumbs-up me-1"></i>Ponto Forte</strong><p className="mb-0 mt-1">{a.ponto_forte}</p></div>}
              {a.ponto_melhoria && <div className="alert alert-danger"><strong><i className="fas fa-arrow-up me-1"></i>O que Melhorar</strong><p className="mb-0 mt-1">{a.ponto_melhoria}</p></div>}
              {a.objetivo_melhoria && <div className="alert" style={{background:'#fff4e0',borderLeft:'4px solid #fd7e14'}}><strong><i className="fas fa-bullseye me-1" style={{color:'#fd7e14'}}></i>Objetivo</strong><p className="mb-0 mt-1">{a.objetivo_melhoria}</p></div>}
              {a.resumo && <div className="card border-info"><div className="card-body"><h6 className="text-info"><i className="fas fa-file-alt me-1"></i>Resumo</h6><p className="mb-0">{a.resumo}</p></div></div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDetalhe(null)}><i className="fas fa-times me-1"></i>Fechar</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="das-page">
      <div className="container-fluid mt-5 text-center">
        <div className="spinner-border text-primary" style={{width:'3rem',height:'3rem'}}></div>
        <p className="mt-3 text-muted">Carregando painel do gestor...</p>
      </div>
    </div>
  );

  return (
    <div className="das-page">
      <div className="container-fluid mt-4">

        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Painel do Gestor — Memor.IA
                      </span>
                      <span className="badge bg-warning text-dark" style={{ fontSize: '15px', fontWeight: '500' }}>
                        <i className="fas fa-users me-1"></i>Visão do Time
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-chart-line header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Dashboard de Performance do Time</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-robot me-2"></i>
                      Análise inteligente de todos os operadores — quem performa, quem precisa de apoio
                    </p>
                    <small className="opacity-75">
                      {filtradas.length} análises · {filtroOp ? 1 : operadores.length} operador(es)
                      {filtroSetor && <span className="badge bg-warning text-dark ms-2">{filtroSetor}</span>}
                      {filtroTipo && (
                        <span className="badge ms-2" style={{ background: TIPOS_REUNIAO.find(t => t.key === filtroTipo)?.cor, color: '#fff' }}>
                          {TIPOS_REUNIAO.find(t => t.key === filtroTipo)?.label}
                        </span>
                      )}
                    </small>
                  </div>
                  <div className="mb-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => navigate('/Contabilidade')}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>Voltar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Aviso de escopo das reuniões de Gestão/1:1 */}
        {gestorSetor && !gestorVeTudo && (
          <div className="alert d-flex align-items-center gap-2 mb-3 py-2 px-3"
            style={{ background: '#eef1fb', border: '1px solid #c9d3f0', color: '#2c3e70', fontSize: '0.9rem' }}>
            <i className="fas fa-user-shield" style={{ color: '#6610f2' }}></i>
            <span>
              Você é <strong>gestor do time {gestorSetor}</strong>. Por privacidade, você só acessa as
              reuniões de <strong>Gestão/1:1</strong> do time {gestorSetor} — as demais informações
              (serviço, vendas, CS e todos os indicadores do time) você vê normalmente.
            </span>
          </div>
        )}

        {/* Filtros: setor + operador */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body py-3">

                {/* Setor */}
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                  <span className="text-muted small fw-semibold" style={{minWidth:60}}>
                    <i className="fas fa-sitemap me-1"></i>Setor:
                  </span>
                  <button
                    className="btn btn-sm"
                    style={chipStyle(!filtroSetor, '#00205b')}
                    onClick={() => { setFiltroSetor(''); setFiltroOp(''); }}
                  >
                    <i className="fas fa-globe me-1"></i>Todos os setores
                    <ChipBadge ativo={!filtroSetor}>{new Set(analises.map(a => a.operador).filter(Boolean)).size}</ChipBadge>
                  </button>
                  {SETORES.map(s => {
                    const qtd = new Set(analises.filter(a => normText(a.setor || '') === normText(s)).map(a => a.operador).filter(Boolean)).size;
                    const ativo = filtroSetor === s;
                    return (
                      <button
                        key={s}
                        className="btn btn-sm"
                        style={chipStyle(ativo, '#00205b')}
                        onClick={() => { setFiltroSetor(ativo ? '' : s); setFiltroOp(''); }}
                      >
                        <i className="fas fa-building me-1" style={{ color: ativo ? '#fff' : '#adb5bd' }}></i>{s}
                        <ChipBadge ativo={ativo}>{qtd}</ChipBadge>
                      </button>
                    );
                  })}
                </div>

                {/* Tipo de análise */}
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                  <span className="text-muted small fw-semibold" style={{minWidth:60}}>
                    <i className="fas fa-brain me-1"></i>Análise:
                  </span>
                  <button
                    className="btn btn-sm"
                    style={chipStyle(!filtroTipo, '#00205b')}
                    onClick={() => { setFiltroTipo(''); setFiltroOp(''); }}
                  >
                    <i className="fas fa-layer-group me-1"></i>Todas
                    <ChipBadge ativo={!filtroTipo}>{analisesPorSetor.length}</ChipBadge>
                  </button>
                  {TIPOS_REUNIAO.map(t => {
                    const qtd = analisesPorSetor.filter(a => normTipo(a.tipo_reuniao) === t.key).length;
                    const ativo = filtroTipo === t.key;
                    return (
                      <button
                        key={t.key}
                        className="btn btn-sm"
                        style={chipStyle(ativo, t.cor)}
                        onClick={() => { setFiltroTipo(ativo ? '' : t.key); setFiltroOp(''); }}
                      >
                        <i className={`fas ${t.icone} me-1`} style={{ color: ativo ? '#fff' : t.cor }}></i>
                        {t.label}
                        <ChipBadge ativo={ativo}>{qtd}</ChipBadge>
                      </button>
                    );
                  })}
                </div>

                {/* Operador */}
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="text-muted small fw-semibold" style={{minWidth:60}}>
                    <i className="fas fa-user me-1"></i>Operador:
                  </span>
                  <button
                    className="btn btn-sm"
                    style={chipStyle(!filtroOp, '#00205b')}
                    onClick={() => setFiltroOp('')}
                  >
                    <i className="fas fa-users me-1"></i>Todos
                    <ChipBadge ativo={!filtroOp}>{baseFiltro.length}</ChipBadge>
                  </button>
                  {operadores.map(op => {
                    const ativo = filtroOp === op;
                    return (
                      <button
                        key={op}
                        className="btn btn-sm"
                        style={chipStyle(ativo, '#00205b')}
                        onClick={() => setFiltroOp(ativo ? '' : op)}
                      >
                        <i className="fas fa-user me-1" style={{ color: ativo ? '#fff' : '#adb5bd' }}></i>{op}
                        <ChipBadge ativo={ativo}>{baseFiltro.filter(a => a.operador === op).length}</ChipBadge>
                      </button>
                    );
                  })}
                </div>

                {/* Diagnóstico do time pela Memor.IA */}
                <div className="mt-3 pt-3 border-top text-center">
                  <button
                    className="btn fw-semibold btn-analise-time"
                    onClick={pedirAnaliseTime}
                    disabled={carregandoAnaliseTime || filtradas.length === 0}
                    style={{
                      background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                      border: 'none', color: '#fff', borderRadius: 12,
                      padding: '0.6rem 2rem',
                      boxShadow: '0 2px 8px rgba(25,135,84,0.3)',
                    }}
                  >
                    {carregandoAnaliseTime
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Analisando o time...</>
                      : <>{filtroSetor ? `Como está a operação do Time ${filtroSetor}?` : (filtroTipo ? `Como está a operação do Time ${TIPOS_REUNIAO.find(t => t.key === filtroTipo)?.label || filtroTipo}?` : 'Como está a operação da Comece?')}</>}
                  </button>
                  <small className="text-muted d-block mt-2">
                    A Memor.IA analisa o recorte filtrado acima: gargalos, pontos fortes, clientes em risco, quem precisa de ajuda e a evolução do time
                  </small>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* ── Modal: Diagnóstico do Time — Memor.IA ── */}
        {showModalAnaliseTime && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1200 }} onClick={() => setShowModalAnaliseTime(false)}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '95vw', width: 1200, margin: '1rem auto' }} onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-1 fw-bold" style={{ color: '#212529' }}>
                      Diagnóstico da Operação — Memor.IA
                    </h5>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <span className="badge" style={{ backgroundColor: '#00205b', color: '#fff', fontSize: '0.85rem' }}>
                        <i className="fas fa-building me-1"></i>
                        Time: {filtroSetor || (filtroTipo ? (TIPOS_REUNIAO.find(t => t.key === filtroTipo)?.label || filtroTipo) : 'Comece (todos os setores)')}
                      </span>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: filtroTipo
                            ? (TIPOS_REUNIAO.find(t => t.key === filtroTipo)?.cor || '#6c757d')
                            : '#6c757d',
                          color: '#fff',
                          fontSize: '0.85rem',
                        }}
                      >
                        <i className="fas fa-brain me-1"></i>
                        {filtroTipo ? (TIPOS_REUNIAO.find(t => t.key === filtroTipo)?.label || filtroTipo) : 'Todas as análises'}
                      </span>
                      <span className="badge bg-secondary" style={{ fontSize: '0.85rem' }}>
                        {filtradas.length} análise{filtradas.length !== 1 ? 's' : ''}
                      </span>
                      <span className="badge bg-light text-dark border" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-users me-1"></i>
                        {operadores.length} operador{operadores.length !== 1 ? 'es' : ''}
                      </span>
                    </div>
                  </div>
                  <button className="btn-close" onClick={() => setShowModalAnaliseTime(false)}></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto', padding: '1.8rem 2.4rem' }}>
                  {carregandoAnaliseTime ? (
                    <div className="text-center py-5">
                      <div className="spinner-border mb-3" style={{ color: '#198754', width: '2.5rem', height: '2.5rem' }}></div>
                      <p className="text-muted fw-semibold">A Memor.IA está analisando a operação do time...</p>
                      <small className="text-muted">Isso pode levar alguns segundos</small>
                    </div>
                  ) : (
                    <div>
                      {renderBarraAudio(analiseTime)}
                      {renderMarkdown(analiseTime)}
                    </div>
                  )}
                </div>
                {!carregandoAnaliseTime && (
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowModalAnaliseTime(false)}>Fechar</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <ContestacoesGestor />

        {filtradas.length === 0 ? (
          <div className="card border-0 shadow-sm"><div className="card-body text-center py-5"><i className="fas fa-inbox fa-3x text-muted mb-3"></i><h5 className="text-muted">{(filtroSetor || filtroTipo) ? 'Nenhuma análise para os filtros selecionados.' : 'Nenhuma análise encontrada.'}</h5></div></div>
        ) : (
          <>
            {/* Insights */}
            {insights.length > 0 && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white"><h6 className="mb-0"><i className="fas fa-robot me-2 text-primary"></i>Insights Automáticos</h6></div>
                    <div className="card-body py-2 d-flex flex-wrap gap-2">
                      {insights.map((ins,i)=>(
                        <div key={i} className={`alert alert-${ins.tipo} mb-0 py-2 px-3`} style={{fontSize:'0.87rem'}}>
                          {ins.tipo==='success'?<i className="fas fa-arrow-up me-1"></i>:ins.tipo==='danger'?<i className="fas fa-arrow-down me-1"></i>:<i className="fas fa-exclamation-triangle me-1"></i>} {ins.txt}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Evolução */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0"><i className="fas fa-chart-area me-2 text-primary"></i>Evolução do Time no Tempo</h6>
                <div className="btn-group btn-group-sm">
                  {[['diario','Diário'],['semanal','Semanal'],['mensal','Mensal']].map(([m,l])=>(
                    <button key={m} className={`btn ${periodo===m?'btn-primary':'btn-outline-secondary'}`} onClick={()=>setPeriodo(m)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="card-body">
                {dadosEvol.length < 2 ? <p className="text-muted text-center py-3">Dados insuficientes para o gráfico.</p> : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={dadosEvol} margin={{top:10,right:20,bottom:5,left:0}}>
                      <defs>
                        <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#0d6efd" stopOpacity={0.35}/>
                          <stop offset="95%" stopColor="#0d6efd" stopOpacity={0.03}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                      <XAxis dataKey="periodo" tickFormatter={v=>fmtPeriodo(v,periodo)} tick={{fontSize:11}}/>
                      <YAxis domain={[0,10]} ticks={[0,2,4,6,8,10]} tick={{fontSize:11}}/>
                      <Tooltip content={<TooltipEvol modo={periodo}/>}/>
                      <ReferenceLine y={7} stroke="#198754" strokeDasharray="5 5" strokeWidth={2} label={{value:'Meta 7',position:'insideTopRight',fontSize:10,fill:'#198754'}}/>
                      <Area type="monotone" dataKey="media" stroke="#0d6efd" strokeWidth={3} fill="url(#gradBlue)" dot={{r:5,fill:'#0d6efd',stroke:'white',strokeWidth:2}} activeDot={{r:7,stroke:'white',strokeWidth:2}} name="Média"/>
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Gargalos + Fortes */}
            <div className="row g-4 mb-4">
              {[
                { titulo:'Principais Gargalos do Time', sub:'Áreas com maior % de falha — precisam de treinamento', cor:'#dc3545', dados:dadosGargalos, fill:'#dc3545', icon:'fa-exclamation-circle', top5:(d)=>d.slice(0,5), taxa:true,
                  ajuda:'Cada barra é o % de falha da competência = falhas ÷ nº de vezes que ela foi avaliada (mínimo de 3 avaliações). Falha = a Memor.IA marcou "não demonstrou" ou deu nota abaixo de 7. Ex.: 9 falhas em 11 avaliações = 82%.' },
                { titulo:'Principais Pontos Fortes',    sub:'O que o time faz bem — replicar e valorizar', cor:'#198754', dados:dadosFortes, fill:'#198754', icon:'fa-thumbs-up', top5:(d)=>d.slice(0,5), taxa:false,
                  ajuda:'Cada barra é quantas vezes o time demonstrou bem a competência (nota 7 ou mais) nas reuniões do filtro. Quanto maior o número, mais recorrente é esse ponto forte.' },
              ].map((s,i)=>{
                const expandido = !!expandirSecao[i];
                const dadosVisiveis = expandido ? s.dados : s.top5(s.dados);
                return (
                <div key={i} className="col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white" style={{borderBottom:`2px solid ${s.cor}`}}>
                      <h6 className="mb-0"><i className={`fas ${s.icon} me-2`} style={{color:s.cor}}></i>{s.titulo}</h6>
                      <small className="text-muted">{s.sub}{s.dados.length > 5 && !expandido ? ' · 5 principais' : ''}</small>
                    </div>
                    <div className="card-body">
                      {s.dados.length===0 ? <p className="text-muted small">Sem dados suficientes.</p> : (
                        <>
                          <div className="d-flex align-items-start gap-2 mb-3 p-2 rounded" style={{background:'#f8f9fa', fontSize:'0.75rem'}}>
                            <i className="fas fa-circle-info mt-1" style={{color:s.cor}}></i>
                            <span className="text-muted">{s.ajuda}</span>
                          </div>
                          <ResponsiveContainer width="100%" height={Math.max(180, dadosVisiveis.length*42)}>
                            <BarChart data={dadosVisiveis} layout="vertical" margin={{left:10,right:s.taxa?52:40}}>
                              <XAxis type="number" tick={{fontSize:11}} allowDecimals={false} domain={s.taxa?[0,100]:undefined}/>
                              <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={130}/>
                              <Tooltip formatter={(v,n,p)=> s.taxa
                                ? [`${v}% de falha (${p.payload.falhas} de ${p.payload.total})`, 'Taxa']
                                : [v,'Ocorrências']}/>
                              <Bar dataKey="value" fill={s.fill} radius={[0,5,5,0]} label={{position:'right',fontSize:11,formatter:(v)=> s.taxa?`${v}%`:v}}/>
                            </BarChart>
                          </ResponsiveContainer>
                          {s.dados.length > 5 && (
                            <div className="text-center mt-2">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setExpandirSecao(prev => ({ ...prev, [i]: !expandido }))}
                              >
                                <i className={`fas fa-chevron-${expandido ? 'up' : 'down'} me-1`}></i>
                                {expandido ? 'Ver menos' : `Ver todas (${s.dados.length})`}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Radar de Competências */}
            <div className="row g-4 mb-4">
              <div className="col-lg-7">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white" style={{ borderBottom: '2px solid #00205b' }}>
                    <h6 className="mb-0">
                      <i className="fas fa-bullseye me-2" style={{ color: '#00205b' }}></i>
                      Radar de Competências
                      {filtroOp && <span className="badge bg-primary ms-2" style={{ fontSize: '0.75rem' }}>{filtroOp}</span>}
                      {!filtroOp && <span className="badge bg-secondary ms-2" style={{ fontSize: '0.75rem' }}>Time completo</span>}
                    </h6>
                    <small className="text-muted">{temIA ? 'Média das notas por competência avaliadas pela Memor.IA' : 'Score 0–10 por área · baseado em pontos fortes e melhorias'}</small>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={dadosRadar.length > 13 ? 760 : 560}>
                      <RadarChart data={dadosRadar} outerRadius={dadosRadar.length > 13 ? '68%' : '75%'}>
                        <PolarGrid stroke="#e9ecef" />
                        <PolarAngleAxis dataKey="competencia" tick={<TickRadar />} />
                        <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fontSize: 11 }} />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="#00205b"
                          fill="#00205b"
                          fillOpacity={0.35}
                        />
                        <Tooltip formatter={v => [`${v}/10`, 'Score']} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white" style={{ borderBottom: '2px solid #00205b' }}>
                    <h6 className="mb-0">
                      <i className="fas fa-list-ol me-2" style={{ color: '#00205b' }}></i>
                      Score por Competência
                    </h6>
                    <small className="text-muted">Verde ≥ 7 · Laranja 5–7 · Vermelho &lt; 5</small>
                  </div>
                  <div className="card-body">
                    <table className="table table-sm table-hover mb-0">
                      <tbody>
                        {dadosRadar.map((d, i) => (
                          <tr key={i}>
                            <td className="small fw-semibold" style={{ width: '45%' }}>{d.competencia}</td>
                            <td style={{ width: '40%' }}>
                              <div className="progress" style={{ height: 10, borderRadius: 5 }}>
                                <div
                                  className={`progress-bar ${d.score >= 7 ? 'bg-success' : d.score >= 5 ? 'bg-warning' : 'bg-danger'}`}
                                  style={{ width: `${d.score * 10}%` }}
                                />
                              </div>
                            </td>
                            <td className="small fw-bold text-end" style={{ width: '15%' }}>
                              <span className={`badge ${d.score >= 7 ? 'bg-success' : d.score >= 5 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                                {d.score}/10
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Impacto palavras */}
            <div className="row g-4 mb-4">
              {[
                { titulo:'Frases Positivas — Impacto na Nota', sub:'Quanto acima da média quando essa palavra aparece', cor:'#198754', icon:'fa-smile', dados:impactoPos, isPos:true },
                { titulo:'Frases Negativas — Impacto na Nota', sub:'Palavras que correlacionam com notas mais baixas', cor:'#dc3545', icon:'fa-frown', dados:impactoNeg, isPos:false },
              ].map((s,i)=>(
                <div key={i} className="col-md-6">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white" style={{borderBottom:`2px solid ${s.cor}`}}>
                      <h6 className="mb-0"><i className={`fas ${s.icon} me-2`} style={{color:s.cor}}></i>{s.titulo}</h6>
                      <small className="text-muted">{s.sub}</small>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-light"><tr><th>Palavra</th><th className="text-center">Ocorr.</th><th className="text-center">Impacto</th></tr></thead>
                          <tbody>
                            {s.dados.length===0 ? <tr><td colSpan={3} className="text-muted text-center py-3">Dados insuficientes (mín. 2 ocorrências).</td></tr>
                            : s.dados.map((p,j)=>(
                              <tr key={j}>
                                <td><strong>{p.palavra}</strong></td>
                                <td className="text-center"><span className="badge bg-secondary">{p.count}×</span></td>
                                <td className="text-center">
                                  <span className={`badge ${p.impacto>=0.5?'bg-success':p.impacto>=-0.5?'bg-primary':p.impacto>=-1?'bg-warning text-dark':'bg-danger'}`}>
                                    {p.impacto>=0?'+':''}{p.impacto}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparação operadores */}
            {comparacaoOp.length > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white">
                  <h6 className="mb-0"><i className="fas fa-users me-2 text-primary"></i>Comparação entre Operadores por Competência</h6>
                  <small className="text-muted">Verde ≥ 7 · Laranja 5–7 · Vermelho &lt; 5 · Cinza = sem dados suficientes</small>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm mb-0" style={{fontSize:'0.84rem'}}>
                      <thead className="table-light">
                        <tr>
                          <th>Operador</th>
                          <th className="text-center">Média</th>
                          <th className="text-center">Reuniões</th>
                          {colunasComp.map(c=><th key={c.key} className="text-center" style={{whiteSpace:'nowrap'}}>{c.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {comparacaoOp.map((op,i)=>(
                          <tr key={i}>
                            <td>
                              <span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:op.cor,marginRight:6}}></span>
                              <strong>{op.operador}</strong>
                            </td>
                            <td className="text-center"><span className={`badge ${badgeNota(op.media)}`}>{op.media}/10</span></td>
                            <td className="text-center"><span className="badge bg-secondary">{op.total}</span></td>
                            {colunasComp.map(c=>{
                              const s = op[c.key];
                              return (
                                <td key={c.key} className="text-center">
                                  {s!==null && s!==undefined
                                    ? <span className="badge" style={{background:corScore(s),fontSize:'0.78rem'}}>{s}/10</span>
                                    : <span className="text-muted" style={{fontSize:'0.75rem'}}>—</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Ranking de operadores — lista com barras finas */}
            {comparacaoOp.length > 1 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white">
                  <h6 className="mb-0"><i className="fas fa-medal me-2" style={{color:'#ffc107'}}></i>Ranking de Operadores — Média Geral</h6>
                  <small className="text-muted">Da maior média para a menor — a marca verde é a meta 7</small>
                </div>
                <div className="card-body py-3">
                  {comparacaoOp.map((op, i) => {
                    const cor = op.media >= 8 ? '#198754' : op.media >= 6 ? '#0d6efd' : op.media >= 4 ? '#fd7e14' : '#dc3545';
                    const medalha = i === 0 ? '' : i === 1 ? '' : i === 2 ? '' : null;
                    return (
                      <div key={i} className="d-flex align-items-center gap-3 py-1" style={{ minHeight: 34 }}>
                        <span className="text-muted text-end flex-shrink-0" style={{ width: 26, fontSize: '0.8rem', fontWeight: 600 }}>
                          {medalha || `${i + 1}º`}
                        </span>
                        <span className="flex-shrink-0 text-truncate" style={{ width: 160, fontSize: '0.88rem', fontWeight: 600 }} title={op.operador}>
                          {op.operador}
                        </span>
                        <div className="progress flex-grow-1" style={{ height: 8, background: '#e9ecef', borderRadius: 4, position: 'relative', overflow: 'visible' }}>
                          <div style={{ width: `${Math.min(op.media * 10, 100)}%`, height: '100%', background: cor, borderRadius: 4 }} />
                          <div style={{ position: 'absolute', left: '70%', top: -3, bottom: -3, width: 2, background: '#19875466' }} title="Meta 7"></div>
                        </div>
                        <span className="badge flex-shrink-0" style={{ background: cor, minWidth: 52 }}>{op.media}/10</span>
                        <span className="badge bg-light text-muted border flex-shrink-0" style={{ fontSize: '0.72rem' }}>
                          {op.total} {op.total !== 1 ? 'reuniões' : 'reunião'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Clientes — Nota Média + Objeções + Produtos */}
            {clientesMedias.length > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0"><i className="fas fa-building me-2 text-primary"></i>Clientes — Nota Média, Objeções e Produtos</h6>
                    <small className="text-muted">Ordenado do pior para o melhor — priorize quem está abaixo de 7</small>
                  </div>
                  <span className="badge bg-secondary">{clientesMedias.length} clientes</span>
                </div>
                {/* Tabela única: nota em barra + objeções e produtos por cliente */}
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0 align-middle" style={{ fontSize: '0.83rem' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: 160 }}>Cliente</th>
                          <th style={{ width: 220, minWidth: 180 }}>Nota Média</th>
                          <th className="text-center" style={{ width: 70 }}>Reuniões</th>
                          <th style={{ maxWidth: 280 }}><i className="fas fa-hand-paper me-1 text-danger"></i>Principais Objeções</th>
                          <th style={{ maxWidth: 280 }}><i className="fas fa-box me-1 text-primary"></i>Produtos / Serviços</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientesMedias.map((c, i) => {
                          const cor = c.media_nota >= 7 ? '#198754' : c.media_nota >= 6 ? '#fd7e14' : '#dc3545';
                          return (
                          <tr key={i}>
                            <td><strong>{c.cliente_empresa}</strong></td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="progress flex-grow-1" style={{ height: 8, background: '#e9ecef', borderRadius: 4, position: 'relative', overflow: 'visible' }}>
                                  <div style={{
                                    width: `${Math.min(c.media_nota * 10, 100)}%`, height: '100%',
                                    background: cor, borderRadius: 4,
                                  }} />
                                  {/* marca da meta 7 */}
                                  <div style={{
                                    position: 'absolute', left: '70%', top: -3, bottom: -3,
                                    width: 2, background: '#19875466',
                                  }} title="Meta 7"></div>
                                </div>
                                <span className="badge flex-shrink-0" style={{ background: cor, minWidth: 52 }}>
                                  {c.media_nota}/10
                                </span>
                              </div>
                            </td>
                            <td className="text-center"><span className="badge bg-secondary">{c.total_reunioes}</span></td>
                            <td style={{ maxWidth: 280 }}>
                              {(c.top_objecoes || []).length === 0
                                ? <span className="text-muted">—</span>
                                : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {c.top_objecoes.map((o, j) => (
                                      <span key={j} className="badge bg-danger bg-opacity-10 text-danger border border-danger"
                                        style={{ fontSize: '0.74rem', fontWeight: 400, whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left', lineHeight: '1.3' }}>
                                        {o}
                                      </span>
                                    ))}
                                  </div>}
                            </td>
                            <td style={{ maxWidth: 280 }}>
                              {(c.top_produtos || []).length === 0
                                ? <span className="text-muted">—</span>
                                : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {c.top_produtos.map((p, j) => (
                                      <span key={j} className="badge bg-primary bg-opacity-10 text-primary border border-primary"
                                        style={{ fontSize: '0.74rem', fontWeight: 400, whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left', lineHeight: '1.3' }}>
                                        {p}
                                      </span>
                                    ))}
                                  </div>}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Reuniões críticas + exemplares */}
            <div className="row g-4 mb-4">
              <div className="col-md-7">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white" style={{borderBottom:'2px solid #dc3545'}}>
                    <h6 className="mb-0"><i className="fas fa-exclamation-triangle me-2 text-danger"></i>Reuniões Críticas — Nota &lt; 6 ({reunioesCriticas.length})</h6>
                    <small className="text-muted">Clique para ver detalhes e identificar padrões</small>
                  </div>
                  <div className="card-body p-0">
                    {reunioesCriticas.length===0
                      ? <div className="text-center py-4 text-success"><i className="fas fa-check-circle fa-2x mb-2 d-block"></i>Nenhuma reunião crítica!</div>
                      : <div className="table-responsive"><table className="table table-sm table-hover mb-0">
                          <thead className="table-light"><tr><th>Operador</th><th>Cliente</th><th>Data</th><th className="text-center">Nota</th><th></th></tr></thead>
                          <tbody>
                            {reunioesCriticas.map((a,i)=>(
                              <tr key={i} style={{cursor:'pointer'}} onClick={()=>setDetalhe(a)}>
                                <td><strong>{a.operador}</strong></td>
                                <td style={{fontSize:'0.82rem'}}>{a.cliente_empresa||'-'}</td>
                                <td style={{fontSize:'0.82rem'}}>{fmtData(a.data_reuniao)}</td>
                                <td className="text-center"><span className={`badge ${badgeNota(a.nota)}`}>{a.nota}/10</span></td>
                                <td><i className="fas fa-chevron-right text-muted"></i></td>
                              </tr>
                            ))}
                          </tbody>
                        </table></div>}
                  </div>
                </div>
              </div>
              <div className="col-md-5">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white" style={{borderBottom:'2px solid #198754'}}>
                    <h6 className="mb-0"><i className="fas fa-star me-2 text-success"></i>Reuniões Exemplares — Nota ≥ 9 ({reunioesExemplares.length})</h6>
                    <small className="text-muted">Referências para o time</small>
                  </div>
                  <div className="card-body p-0">
                    {reunioesExemplares.length===0
                      ? <div className="text-center py-4 text-muted"><i className="fas fa-search fa-2x mb-2 d-block"></i><small>Sem reuniões exemplares ainda.</small></div>
                      : <div className="table-responsive"><table className="table table-sm table-hover mb-0">
                          <thead className="table-light"><tr><th>Operador</th><th>Data</th><th className="text-center">Nota</th></tr></thead>
                          <tbody>
                            {reunioesExemplares.map((a,i)=>(
                              <tr key={i} style={{cursor:'pointer'}} onClick={()=>setDetalhe(a)}>
                                <td><strong>{a.operador}</strong></td>
                                <td style={{fontSize:'0.82rem'}}>{fmtData(a.data_reuniao)}</td>
                                <td className="text-center"><span className="badge bg-success">{a.nota}/10</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table></div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Sugestões de treinamento */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white" style={{borderBottom:'2px solid #6610f2'}}>
                <h6 className="mb-0"><i className="fas fa-graduation-cap me-2" style={{color:'#6610f2'}}></i>Sugestões de Treinamento por Operador</h6>
                <small className="text-muted">Baseado na competência com pior score — priorize quem tem menor média</small>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr><th>Operador</th><th className="text-center">Média</th><th className="text-center">Evolução</th><th>Área Crítica</th><th>Score</th><th>Sugestão de Ação</th></tr>
                    </thead>
                    <tbody>
                      {sugestoes.map((s,i)=>(
                        <tr key={i}>
                          <td>
                            <span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:s.cor,marginRight:6}}></span>
                            <strong>{s.operador}</strong>
                          </td>
                          <td className="text-center"><span className={`badge ${badgeNota(s.media)}`}>{s.media}/10</span></td>
                          <td className="text-center">
                            {s.evo!==0
                              ? <span className={`badge ${s.evo>=0?'bg-success':'bg-danger'}`}>{s.evo>=0?'↑':'↓'} {Math.abs(s.evo)}</span>
                              : <span className="text-muted">—</span>}
                          </td>
                          <td><span className="badge" style={{background:'#6610f2'}}>{s.sugestao}</span></td>
                          <td>
                            {s.piorScore<11
                              ? <div className="d-flex align-items-center gap-2"><div className="progress" style={{width:'55px',height:'6px'}}><div className="progress-bar bg-danger" style={{width:`${(s.piorScore||0)*10}%`}}></div></div><small>{s.piorScore}/10</small></div>
                              : <span className="text-muted">—</span>}
                          </td>
                          <td style={{fontSize:'0.82rem',color:'#444'}}>{SUGESTAO_TEXTO[s.sugestao]||'Avaliar pontos gerais de melhoria'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* Histórico completo de reuniões */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="fas fa-list me-2 text-secondary"></i>
                  Histórico de Reuniões Analisadas
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-secondary">{historicoAgrupado.length} reuniões</span>
                  {(tblOperador||tblCodigo||tblEmpresa||tblTipo||tblData||tblNota) && (
                    <button className="btn btn-sm btn-outline-danger" onClick={()=>{setTblOperador('');setTblCodigo('');setTblEmpresa('');setTblTipo('');setTblData('');setTblNota('');}}>
                      <i className="fas fa-times me-1"></i>Limpar filtros
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0" style={{fontSize:'0.83rem'}}>
                    <thead className="table-light">
                      <tr>
                        <th>Código</th>
                        <th>Empresa</th>
                        <th>Tipo</th>
                        <th>Data</th>
                        <th>Operadores</th>
                        <th className="text-center">Nota da Reunião</th>
                        <th></th>
                      </tr>
                      {/* linha de filtros */}
                      <tr style={{background:'#f8f9fa'}}>
                        <td>
                          <input className="form-control form-control-sm" placeholder="Filtrar..." value={tblCodigo}
                            onChange={e=>setTblCodigo(e.target.value)} style={{minWidth:90}}/>
                        </td>
                        <td>
                          <input className="form-control form-control-sm" placeholder="Filtrar..." value={tblEmpresa}
                            onChange={e=>setTblEmpresa(e.target.value)} style={{minWidth:110}}/>
                        </td>
                        <td>
                          <input className="form-control form-control-sm" placeholder="Filtrar..." value={tblTipo}
                            onChange={e=>setTblTipo(e.target.value)} style={{minWidth:90}}/>
                        </td>
                        <td>
                          <input className="form-control form-control-sm" placeholder="AAAA-MM-DD" value={tblData}
                            onChange={e=>setTblData(e.target.value)} style={{minWidth:110}}/>
                        </td>
                        <td>
                          <input className="form-control form-control-sm" placeholder="Filtrar..." value={tblOperador}
                            onChange={e=>setTblOperador(e.target.value)} style={{minWidth:100}}/>
                        </td>
                        <td>
                          <select className="form-select form-select-sm" value={tblNota} onChange={e=>setTblNota(e.target.value)} style={{minWidth:80}}>
                            <option value="">Todas</option>
                            <option value=">=8">≥ 8</option>
                            <option value="6-8">6 – 8</option>
                            <option value="<6">&lt; 6</option>
                          </select>
                        </td>
                        <td></td>
                      </tr>
                    </thead>
                    <tbody>
                      {historicoAgrupado.length === 0 ? (
                        <tr><td colSpan={7} className="text-center text-muted py-4">Nenhuma reunião encontrada com esses filtros.</td></tr>
                      ) : historicoAgrupado.map((a, i) => (
                        <tr key={i} style={{cursor:'pointer'}} onClick={()=>abrirReuniao(a.codigo_reuniao)}>
                          <td><span className="badge" style={{background:'#fd7e14',fontSize:'0.72rem'}}>{a.codigo_reuniao}</span></td>
                          <td>{a.cliente_empresa || '—'}</td>
                          <td><span className="badge bg-secondary" style={{fontSize:'0.7rem'}}>{labelTipoReuniao(a.tipo_reuniao)}</span></td>
                          <td>{fmtData(a.data_reuniao)}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {a.operadores_reuniao.map((op, j) => (
                                <span key={j} className="badge bg-light text-dark border" style={{fontSize:'0.72rem', fontWeight: 500}}>
                                  {op.nome} <span className={`ms-1 badge ${badgeNota(op.nota)}`} style={{fontSize:'0.62rem'}}>{op.nota}</span>
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="text-center">
                            {a.nota_reuniao !== null && a.nota_reuniao !== undefined
                              ? <span className={`badge ${badgeNota(a.nota_reuniao)}`}>{a.nota_reuniao}/10</span>
                              : <span className="text-muted">—</span>}
                          </td>
                          <td><i className="fas fa-chevron-right text-muted"></i></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {renderModal()}

        {/* ── Modal: análise completa da reunião ── */}
        {reuniaoDetalhe && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1250 }}
            onClick={() => setReuniaoDetalhe(null)}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '950px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                {carregandoReuniao || reuniaoDetalhe.carregando ? (
                  <div className="modal-body text-center py-5">
                    <div className="spinner-border text-primary mb-3" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                    <p className="text-muted mb-0">Carregando a análise completa...</p>
                  </div>
                ) : (() => {
                  const r = reuniaoDetalhe.reuniao;
                  const ops = reuniaoDetalhe.operadores || [];
                  return (
                    <>
                      <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                        <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                          <i className="fas fa-video me-2"></i>
                          {r.codigo_reuniao} — {r.cliente_empresa || r.assunto || ''}
                        </h5>
                        <button className="btn-close" onClick={() => setReuniaoDetalhe(null)}></button>
                      </div>
                      <div className="modal-body">
                        {/* KPIs da reunião */}
                        <div className="row g-3 mb-3">
                          <div className="col-6 col-md-3 text-center">
                            <small className="text-muted d-block">Nota da Reunião</small>
                            <span className={`badge fs-5 ${badgeNota(r.nota)}`}>{r.nota != null ? `${r.nota}/10` : '—'}</span>
                          </div>
                          <div className="col-6 col-md-3 text-center">
                            <small className="text-muted d-block">Tipo</small>
                            <span className="badge bg-secondary">{labelTipoReuniao(r.tipo_reuniao)}</span>
                          </div>
                          <div className="col-6 col-md-3 text-center">
                            <small className="text-muted d-block">Data</small>
                            <strong>{fmtData(r.data_reuniao)}{r.horario ? ` · ${r.horario}` : ''}</strong>
                          </div>
                          <div className="col-6 col-md-3 text-center">
                            <small className="text-muted d-block">Classificação</small>
                            <span className={`badge ${
                              r.classificacao === 'muito_boa' ? 'bg-success' :
                              r.classificacao === 'boa' ? 'bg-primary' :
                              r.classificacao === 'critica' ? 'bg-danger' : 'bg-secondary'
                            }`}>{(r.classificacao || '-').replace('_', ' ').toUpperCase()}</span>
                          </div>
                        </div>

                        {r.analisado_por && (
                          <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-user-check me-1"></i>Analisada por <strong>{r.analisado_por}</strong>
                          </div>
                        )}
                        {r.resumo && (
                          <div className="card border-info mb-3">
                            <div className="card-body py-2">
                              <h6 className="text-info mb-1"><i className="fas fa-file-alt me-1"></i> Resumo</h6>
                              <p className="mb-0" style={{ fontSize: '0.9rem' }}>{r.resumo}</p>
                            </div>
                          </div>
                        )}

                        {/* Contato / interesse */}
                        {(r.cliente_nome || r.interesse_nivel || r.cliente_orcamento) && (
                          <div className="d-flex flex-wrap gap-3 mb-3">
                            {r.cliente_nome && (<div><div className="text-muted" style={{fontSize:'0.7rem',fontWeight:700}}>CONTATO</div><div style={{fontSize:'0.88rem',fontWeight:600}}>{r.cliente_nome}{r.cliente_cargo ? ` · ${r.cliente_cargo}` : ''}</div></div>)}
                            {r.interesse_nivel && (<div><div className="text-muted" style={{fontSize:'0.7rem',fontWeight:700}}>INTERESSE</div><div style={{fontSize:'0.88rem',fontWeight:600}}>{r.interesse_nivel}</div></div>)}
                            {r.estagio_venda && r.estagio_venda !== 'não se aplica' && (<div><div className="text-muted" style={{fontSize:'0.7rem',fontWeight:700}}>ESTÁGIO</div><div style={{fontSize:'0.88rem',fontWeight:600}}>{r.estagio_venda}</div></div>)}
                            {r.cliente_orcamento && (<div><div className="text-muted" style={{fontSize:'0.7rem',fontWeight:700}}>ORÇAMENTO</div><div style={{fontSize:'0.88rem',fontWeight:600}}>{r.cliente_orcamento}</div></div>)}
                          </div>
                        )}

                        {r.cliente_dor_principal && (
                          <div className="alert py-2 px-3 mb-3" style={{ background: '#fff4e0', borderLeft: '4px solid #fd7e14' }}>
                            <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-heart-pulse me-1" style={{ color: '#fd7e14' }}></i> DOR PRINCIPAL</strong>
                            <p className="mb-0 mt-1" style={{ fontSize: '0.88rem' }}>{r.cliente_dor_principal}</p>
                          </div>
                        )}
                        {r.oportunidade && (
                          <div className="alert alert-success py-2 px-3 mb-3">
                            <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-lightbulb me-1"></i>OPORTUNIDADE</strong>
                            <p className="mb-0 mt-1" style={{ fontSize: '0.88rem' }}>{r.oportunidade}</p>
                          </div>
                        )}

                        {(r.pontos_criticos || []).length > 0 && (
                          <div className="mb-3">
                            <h6 className="fw-bold mb-2"><i className="fas fa-triangle-exclamation me-2 text-danger"></i>Pontos Críticos</h6>
                            {r.pontos_criticos.map((p, i) => {
                              const ehRisco = (p?.tipo || '') === 'risco';
                              return (
                                <div key={i} className="d-flex gap-2 mb-2 p-2 rounded" style={{ background: ehRisco ? '#fdecec' : '#eaf7ef', fontSize: '0.87rem' }}>
                                  <span className="badge flex-shrink-0" style={{ background: ehRisco ? '#dc3545' : '#198754', height: 'fit-content' }}>{ehRisco ? 'Risco' : 'Oportunidade'}</span>
                                  <span>{p?.descricao || (typeof p === 'string' ? p : '')}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="row g-3 mb-3">
                          {(r.objecoes || []).length > 0 && (
                            <div className="col-md-6">
                              <h6 className="fw-bold mb-2"><i className="fas fa-shield-halved me-2" style={{ color: '#0d6efd' }}></i>Objeções</h6>
                              {r.objecoes.map((o, i) => (
                                <div key={i} className="d-flex gap-2 mb-1" style={{ fontSize: '0.87rem' }}>
                                  <i className="fas fa-circle-dot flex-shrink-0" style={{ marginTop: 5, fontSize: '0.6rem', color: '#0d6efd' }}></i>
                                  <span>{typeof o === 'string' ? o : (o?.objecao || o?.descricao || '')}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {(r.proximos_passos || []).length > 0 && (
                            <div className="col-md-6">
                              <h6 className="fw-bold mb-2"><i className="fas fa-list-check me-2 text-success"></i>Próximos Passos</h6>
                              {r.proximos_passos.map((p, i) => {
                                const texto = typeof p === 'string' ? p
                                  : [p?.responsavel, p?.acao].filter(Boolean).join(' — ') + (p?.prazo ? ` (até ${p.prazo})` : '');
                                return (
                                  <div key={i} className="d-flex gap-2 mb-1" style={{ fontSize: '0.87rem' }}>
                                    <i className="fas fa-arrow-right flex-shrink-0 text-success" style={{ marginTop: 4, fontSize: '0.72rem' }}></i>
                                    <span>{texto}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {(r.produtos_servicos || []).length > 0 && (
                          <div className="mb-3">
                            <h6 className="fw-bold mb-2"><i className="fas fa-box-open me-2" style={{ color: '#6610f2' }}></i>Produtos / Serviços de Interesse</h6>
                            <div className="d-flex flex-wrap gap-1">
                              {r.produtos_servicos.map((p, i) => (
                                <span key={i} className="badge bg-primary bg-opacity-10 text-primary border border-primary" style={{ fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'normal', textAlign: 'left' }}>
                                  {typeof p === 'string' ? p : (p?.nome || '')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Análise por operador */}
                        <h6 className="fw-bold mb-2 mt-4"><i className="fas fa-users me-2" style={{ color: '#00205b' }}></i>Análise por Operador ({ops.length})</h6>
                        {ops.map((op, i) => (
                          <div key={i} className="card mb-3" style={{ borderLeft: '4px solid #00205b' }}>
                            <div className="card-body py-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong style={{ fontSize: '0.95rem' }}><i className="fas fa-user-circle me-2 text-muted"></i>{op.operador}</strong>
                                <span className={`badge ${badgeNota(op.nota)}`}>{op.nota != null ? `${op.nota}/10` : '—'}</span>
                              </div>
                              {op.ponto_forte && (
                                <div className="mb-1" style={{ fontSize: '0.85rem' }}>
                                  <i className="fas fa-thumbs-up text-success me-2"></i>{op.ponto_forte}
                                </div>
                              )}
                              {op.ponto_melhoria && (
                                <div className="mb-2" style={{ fontSize: '0.85rem' }}>
                                  <i className="fas fa-arrow-up text-danger me-2"></i>{op.ponto_melhoria}
                                </div>
                              )}
                              {(op.competencias || []).length > 0 && (
                                <div className="d-flex flex-wrap gap-1">
                                  {op.competencias.map((c, j) => {
                                    if (!c || !c.competencia) return null;
                                    const naoAplica = c.status === 'nao_se_aplica';
                                    const demonstrou = c.status === 'demonstrou';
                                    return (
                                      <span key={j} className="badge" title={c.evidencia || ''} style={{
                                        background: naoAplica ? '#e9ecef' : demonstrou ? '#d1e7dd' : '#f8d7da',
                                        color: naoAplica ? '#6c757d' : demonstrou ? '#0f5132' : '#842029',
                                        fontSize: '0.74rem', fontWeight: 500,
                                      }}>
                                        {LABEL_COMPETENCIA[c.competencia] || c.competencia}
                                        {!naoAplica && c.nota !== null && c.nota !== undefined && <> · {c.nota}</>}
                                        {naoAplica && <> · n/a</>}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                        {r.observacoes && (
                          <div className="p-3 rounded-3" style={{ background: '#f8f9fa', fontSize: '0.87rem' }}>
                            <strong style={{ fontSize: '0.78rem' }}><i className="fas fa-robot me-1"></i> OBSERVAÇÕES DA MEMOR.IA</strong>
                            <p className="mb-0 mt-1 fst-italic text-muted">{r.observacoes}</p>
                          </div>
                        )}
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={() => setReuniaoDetalhe(null)}>
                          <i className="fas fa-times me-1"></i>Fechar
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        <style>{`
          .modal.show { display: block; }
          @keyframes pulseMemora {
            0% { box-shadow: 0 2px 8px rgba(25,135,84,0.3), 0 0 0 0 rgba(32,201,151,0.55); }
            70% { box-shadow: 0 2px 8px rgba(25,135,84,0.3), 0 0 0 14px rgba(32,201,151,0); }
            100% { box-shadow: 0 2px 8px rgba(25,135,84,0.3), 0 0 0 0 rgba(32,201,151,0); }
          }
          .btn-analise-time:hover:not(:disabled) {
            animation: pulseMemora 1.2s ease-out infinite;
            transform: scale(1.03);
          }
          .btn-analise-time { transition: transform 0.2s ease; }
        `}</style>
      </div>
    </div>
  );
};

export default Analise_gestor_memora;
